/**
 * ============================================================================
 * I18N PROVIDER (Internationalization)
 * ============================================================================
 * 
 * Provides internationalization support
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useUserStore } from '../store';

const I18nContext = createContext(null);

// Default translations
const translations = {
  en: {
    common: {
      loading: 'Loading...',
      error: 'An error occurred',
      success: 'Success',
      cancel: 'Cancel',
      confirm: 'Confirm',
      save: 'Save',
      delete: 'Delete',
      edit: 'Edit',
      close: 'Close',
    },
    auth: {
      login: 'Login',
      logout: 'Logout',
      signup: 'Sign Up',
      email: 'Email',
      phone: 'Phone',
      password: 'Password',
    },
    transaction: {
      create: 'Create Transaction',
      amount: 'Amount',
      description: 'Description',
      status: 'Status',
    },
  },
  fr: {
    common: {
      loading: 'Chargement...',
      error: 'Une erreur est survenue',
      success: 'Succès',
      cancel: 'Annuler',
      confirm: 'Confirmer',
      save: 'Enregistrer',
      delete: 'Supprimer',
      edit: 'Modifier',
      close: 'Fermer',
    },
    auth: {
      login: 'Connexion',
      logout: 'Déconnexion',
      signup: 'S\'inscrire',
      email: 'E-mail',
      phone: 'Téléphone',
      password: 'Mot de passe',
    },
    transaction: {
      create: 'Créer une transaction',
      amount: 'Montant',
      description: 'Description',
      status: 'Statut',
    },
  },
};

/**
 * I18n Provider Component
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @param {string} props.defaultLocale - Default locale (default: 'en')
 * @returns {JSX.Element}
 */
export const I18nProvider = ({ children, defaultLocale = 'en' }) => {
  const { preferences, updatePreference } = useUserStore();
  const [locale, setLocaleState] = useState(
    preferences?.language || defaultLocale
  );
  const [translationsMap, setTranslationsMap] = useState(translations);

  // Sync with user preferences
  useEffect(() => {
    if (preferences?.language && preferences.language !== locale) {
      setLocaleState(preferences.language);
    }
  }, [preferences?.language, locale]);

  // Update locale
  const setLocale = useCallback(
    (newLocale) => {
      setLocaleState(newLocale);
      updatePreference('preferences', 'language', newLocale);
    },
    [updatePreference]
  );

  // Translation function
  const t = useCallback(
    (key, params = {}) => {
      const keys = key.split('.');
      let value = translationsMap[locale];

      for (const k of keys) {
        value = value?.[k];
        if (value === undefined) {
          // Fallback to English
          value = translationsMap.en;
          for (const k2 of keys) {
            value = value?.[k2];
            if (value === undefined) {
              return key; // Return key if translation not found
            }
          }
          break;
        }
      }

      // Replace params
      if (typeof value === 'string' && params) {
        return value.replace(/\{\{(\w+)\}\}/g, (match, paramKey) => {
          return params[paramKey] !== undefined ? params[paramKey] : match;
        });
      }

      return value || key;
    },
    [locale, translationsMap]
  );

  // Format currency
  const formatCurrency = useCallback(
    (amount, currency = preferences?.currency || 'XAF') => {
      return new Intl.NumberFormat(locale === 'fr' ? 'fr-CM' : 'en-CM', {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(amount);
    },
    [locale, preferences?.currency]
  );

  // Format date (guarded: invalid/undefined date returns empty string to avoid RangeError)
  const formatDate = useCallback(
    (date, options = {}) => {
      if (date == null || date === '') return '';
      const d = new Date(date);
      if (Number.isNaN(d.getTime())) return '';
      return new Intl.DateTimeFormat(locale === 'fr' ? 'fr-CM' : 'en-CM', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        ...options,
      }).format(d);
    },
    [locale]
  );

  // Format time (guarded: invalid/undefined date returns empty string)
  const formatTime = useCallback(
    (date, options = {}) => {
      if (date == null || date === '') return '';
      const d = new Date(date);
      if (Number.isNaN(d.getTime())) return '';
      return new Intl.DateTimeFormat(locale === 'fr' ? 'fr-CM' : 'en-CM', {
        hour: '2-digit',
        minute: '2-digit',
        ...options,
      }).format(d);
    },
    [locale]
  );

  // Format number
  const formatNumber = useCallback(
    (number, options = {}) => {
      return new Intl.NumberFormat(locale === 'fr' ? 'fr-CM' : 'en-CM', options).format(number);
    },
    [locale]
  );

  // Format phone number for display
  const formatPhoneNumber = useCallback(
    (phone) => {
      if (!phone) return '';
      // Format: +237 6XX XXX XXX
      const cleaned = phone.replace(/\D/g, '');
      if (cleaned.startsWith('237')) {
        const number = cleaned.slice(3);
        if (number.length === 9) {
          return `+237 ${number.slice(0, 3)} ${number.slice(3, 6)} ${number.slice(6)}`;
        }
      }
      return phone;
    },
    []
  );

  const value = {
    locale,
    language: locale, // Alias for easier access
    setLocale,
    setLanguage: setLocale, // Alias so header/UI can use setLanguage
    t,
    formatCurrency,
    formatDate,
    formatTime,
    formatNumber,
    formatPhoneNumber,
    translations: translationsMap[locale] || translationsMap.en,
  };

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
};

/**
 * Use i18n context
 * @returns {Object} I18n context value
 */
export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    // Fallback to basic implementation
    return {
      locale: 'en',
      language: 'en',
      setLocale: () => {},
      setLanguage: () => {},
      t: (key) => key,
      formatCurrency: (amount) => `${amount} XAF`,
      formatDate: (date) => (date ? new Date(date).toLocaleDateString() : ''),
      formatTime: (date) => (date ? new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''),
      formatNumber: (number) => number.toLocaleString(),
      formatPhoneNumber: (phone) => phone,
      translations: translations.en,
    };
  }
  return context;
};

export default I18nProvider;
